import {
  type ReactElement,
  cloneElement,
  useEffect,
  useRef,
  useState,
} from 'react';

import { BaseTooltip } from '@gbs-monorepo-packages/common';

interface ITextWithTooltipProps {
  text: string;
  children: ReactElement;
  dataCy?: string;
  maxLengthForTooltip?: number;
}

export const TextWithTooltip = ({
  text,
  children,
  dataCy = 'text-with-tooltip',
  maxLengthForTooltip = 80,
}: ITextWithTooltipProps): JSX.Element => {
  const textRef = useRef<HTMLElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const element = textRef.current;
        const truncated = element.scrollHeight > element.clientHeight;
        setIsTruncated(truncated);
      }
    };

    const animationFrameId = requestAnimationFrame(checkTruncation);

    return () => cancelAnimationFrame(animationFrameId);
  }, [text]);

  const setRef = (node: HTMLElement | null) => {
    textRef.current = node;
  };

  const textElement = cloneElement(children, {
    ref: setRef,
    'data-cy': dataCy,
    children: text,
  });

  const shouldShowTooltip = isTruncated || text.length > maxLengthForTooltip;

  return shouldShowTooltip ? (
    <BaseTooltip message={text} dataCy={`${dataCy}-tooltip`}>
      {textElement}
    </BaseTooltip>
  ) : (
    textElement
  );
};
